/*
 * ============================================================================
 * EVEDIA — subscription-aware free shipping bar
 * ----------------------------------------------------------------------------
 * Replaces the stock Safe As Milk <free-shipping-bar> component.
 *
 * WHY THIS EXISTS
 * The stock component knows about exactly one threshold (theme settings >
 * Free shipping bar > Minimum purchase amount, currently $99) and measures
 * the whole cart against it. Evedia has two thresholds:
 *
 *   • One-time orders  — free USPS Priority at $99+  (a shipping RATE with a
 *                        TOTAL_PRICE >= $99 condition on the General profile)
 *   • Subscriptions    — free shipping at $35+       (the "Subscription
 *                        Shipping Discount" automatic discount, which is
 *                        subscription-only and keys off the subscription
 *                        line-item subtotal)
 *
 * So a subscriber whose cart already qualifies was being shown "$99 to go".
 * This component reads the selling plan off each cart line and shows the
 * threshold that actually applies to them.
 *
 * BEHAVIOUR
 *   No subscription lines  → unchanged: whole cart vs. the $99 rate.
 *   Subscription lines     → primary bar tracks the SUBSCRIPTION subtotal
 *                            against $35.
 *   Mixed cart             → primary bar as above, plus a secondary note
 *                            stating the $99 one-time threshold and the
 *                            remaining amount. Phrased as a threshold, not a
 *                            promise, because Shopify evaluates the two
 *                            independently.
 *
 * The tag name and every class name are unchanged so all existing CSS
 * (component-cart.css, v26-cart.css) applies untouched. The confetti goal
 * animation is not reimplemented — v26 hides it in CSS.
 *
 * Copy lives in snippets/cart-free-shipping-bar.liquid and arrives on
 * data-copy-* attributes, so wording is edited in one place. The defaults
 * below are only a fallback.
 * ============================================================================
 */

import Cart from "cart-store";

import { formatMoney } from "utils";

const DEFAULT_COPY = {
  onetimeInitial: 'Spend <b><span class="money">[amount]</span></b> to get free shipping',
  onetimePartial: 'Spend <b><span class="money">[amount]</span> more</b> to get free shipping',
  onetimeMet: "Congratulations! You get <b>free</b> shipping.",
  subPartial:
    'Add <b><span class="money">[amount]</span> more</b> — subscriptions ship free over [minimum]',
  subMet: "Your subscription ships free.",
  notePartial: "One-time items ship free on orders [minimum]+ — [amount] to go",
  noteMet: "Your one-time items ship free too.",
};

class FreeShippingBar extends HTMLElement {
  #boundModalEvent;
  #parentModal;

  constructor() {
    super();
    this.#boundModalEvent = this.#onModalEvent.bind(this);
  }

  connectedCallback() {
    this.textElement = this.querySelector("free-shipping-bar-text");
    this.trackElement = this.querySelector("free-shipping-bar-track");
    this.messageElement = this.querySelector("[data-fsb-message]");
    this.noteElement = this.querySelector("[data-fsb-note]");

    this.oneTimeMinimum = Number(this.getAttribute("minimum-value")) || 0;
    this.subscriptionMinimum = Number(this.getAttribute("data-sub-minimum")) || 0;
    this.revealDelay = Number(this.getAttribute("reveal-delay")) || 0;

    this.copy = { ...DEFAULT_COPY };
    Object.keys(DEFAULT_COPY).forEach((key) => {
      const attribute = `data-copy-${key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}`;
      const value = this.getAttribute(attribute);
      if (value) this.copy[key] = value;
    });

    // The server-rendered markup is already correct for the cart as it was on
    // page load, so we deliberately do NOT render here — only on cart changes.
    this.unsubscribe = Cart.subscribe((state) => {
      this.render(state.cart);
    });

    this.#parentModal = this.closest("modal-dialog, popup-dialog");

    if (this.#parentModal) {
      this.#parentModal.addEventListener("on:modal:opened", this.#boundModalEvent);
      this.#parentModal.addEventListener("on:modal:closed", this.#boundModalEvent);
    } else {
      this.visibilityObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            setTimeout(() => {
              if (entry.isIntersecting) {
                entry.target.classList.add("is-visible");
                if (this.visibilityObserver) this.visibilityObserver.disconnect();
              }
            }, this.revealDelay);
          });
        },
        { rootMargin: "0px", threshold: 0.95 }
      );
      this.visibilityObserver.observe(this);
    }
  }

  disconnectedCallback() {
    this.classList.remove("is-visible");
    if (this.visibilityObserver) this.visibilityObserver.disconnect();
    if (this.#parentModal) {
      this.#parentModal.removeEventListener("on:modal:opened", this.#boundModalEvent);
      this.#parentModal.removeEventListener("on:modal:closed", this.#boundModalEvent);
    }
    if (this.unsubscribe) this.unsubscribe();
  }

  /**
   * Split the cart into subscription and one-time subtotals.
   * A line is a subscription line when Shopify has attached a selling plan
   * allocation to it (native selling plans, which is what Subscribe & Save
   * uses). Falls back gracefully if the shape is ever missing.
   */
  static totals(cart) {
    const items = (cart && cart.items) || [];
    let subscription = 0;
    let oneTime = 0;
    let subscriptionLines = 0;

    items.forEach((item) => {
      const price = Number(
        item.final_line_price != null ? item.final_line_price : item.line_price || 0
      );
      if (item.selling_plan_allocation) {
        subscription += price;
        subscriptionLines += 1;
      } else {
        oneTime += price;
      }
    });

    const total = Number(cart && cart.total_price != null ? cart.total_price : subscription + oneTime);

    return { subscription, oneTime, total, hasSubscription: subscriptionLines > 0 };
  }

  #money(cents) {
    // Shopify's own money_format token — unrelated to the [amount] placeholder
    // used by the copy strings above.
    const format = (window.theme && window.theme.money_format) || "${{amount}}";
    return formatMoney(cents, format).replace(/[.,]00$/, "");
  }

  #fill(template, amountCents, minimumCents) {
    return template
      .replace(/\[amount\]/g, this.#money(amountCents))
      .replace(/\[minimum\]/g, this.#money(minimumCents));
  }

  render(cart) {
    const { subscription, oneTime, total, hasSubscription } = FreeShippingBar.totals(cart);

    let value;
    let minimum;
    let message;
    let note = "";

    if (hasSubscription && this.subscriptionMinimum > 0) {
      value = subscription;
      minimum = this.subscriptionMinimum;
      const remaining = Math.max(minimum - subscription, 0);

      message =
        remaining > 0
          ? this.#fill(this.copy.subPartial, remaining, minimum)
          : this.#fill(this.copy.subMet, 0, minimum);

      if (oneTime > 0 && this.oneTimeMinimum > 0) {
        const oneTimeRemaining = Math.max(this.oneTimeMinimum - total, 0);
        note =
          oneTimeRemaining > 0
            ? this.#fill(this.copy.notePartial, oneTimeRemaining, this.oneTimeMinimum)
            : this.#fill(this.copy.noteMet, 0, this.oneTimeMinimum);
      }
    } else {
      value = total;
      minimum = this.oneTimeMinimum;
      const remaining = Math.max(minimum - total, 0);

      if (total === 0) {
        message = this.#fill(this.copy.onetimeInitial, minimum, minimum);
      } else if (remaining > 0) {
        message = this.#fill(this.copy.onetimePartial, remaining, minimum);
      } else {
        message = this.#fill(this.copy.onetimeMet, 0, minimum);
      }
    }

    const percentLeft =
      minimum > 0 ? Math.round((Math.max(minimum - value, 0) / minimum) * 100) : 0;

    this.setAttribute("value", value);
    this.setAttribute("minimum-value", minimum);

    // Only the message span is rewritten, so the icon (if any) survives and the
    // message stays a single flex item inside .free-shipping-bar__text.
    const target = this.messageElement || this.textElement;
    if (target) target.innerHTML = message;
    if (this.trackElement) this.trackElement.style.setProperty("--displacement", `${percentLeft}%`);
    if (this.noteElement) {
      this.noteElement.innerHTML = note;
      if (note) {
        this.noteElement.removeAttribute("hidden");
      } else {
        this.noteElement.setAttribute("hidden", "");
      }
    }
  }

  #onModalEvent(event) {
    if (event.type === "on:modal:opened") {
      this.classList.add("is-visible");
    } else if (event.type === "on:modal:closed") {
      this.classList.remove("is-visible");
    }
  }
}

if (!customElements.get("free-shipping-bar")) {
  customElements.define("free-shipping-bar", FreeShippingBar);
}
